/**
 * budget-display — shared presentation helpers for the Budget Boost views.
 *
 * Centralizes the price-change column math and the colour/heatmap logic so the
 * verdict cards (default view) and the compact table share one source of truth.
 */

import {
  PPM_THRESHOLDS,
  TIER_A_COLUMNS,
  TIER_B_POOR_THRESHOLD,
} from "./constants";
import type {
  ConstructorBudgetCalculation,
  DriverBudgetCalculation,
} from "./price-change-engine";

export type BudgetItem = DriverBudgetCalculation | ConstructorBudgetCalculation;

/** A driver row carries `driverCode`; constructor rows do not. */
export function isDriverCalc(
  item: BudgetItem,
): item is DriverBudgetCalculation {
  return "driverCode" in item;
}

/** Returns the PPM threshold for a given price-change column. */
export function getColumnThresholdPPM(col: string): number | null {
  if (col === "+0.6" || col === "+0.3") return PPM_THRESHOLDS.GREAT; // ≥1.2 PPM
  if (col === "+0.2" || col === "+0.1") return PPM_THRESHOLDS.GOOD; // ≥0.9 PPM
  if (col === "-0.2" || col === "-0.1") return PPM_THRESHOLDS.GOOD; // <0.9 PPM
  if (col === "-0.6") return TIER_B_POOR_THRESHOLD; // <0.4 PPM (Tier B)
  if (col === "-0.3") return PPM_THRESHOLDS.POOR; // <0.6 PPM (Tier A)
  return null;
}

/**
 * Points needed this race for a given price-change column, accounting for the
 * 3-race rolling average PPM used by F1 Fantasy's price change system.
 *
 * Formula: pts_needed = N × thresholdPPM × currentPrice - ptsR2 - ptsR1
 * where N = number of races in the rolling window (1, 2, or 3).
 *
 * Returns the integer points needed, or null when not applicable.
 */
export function columnPointsNeeded(
  col: string,
  currentPrice: number,
  pointsRMinus2: number | null,
  pointsRMinus1: number | null,
): number | null {
  const thresholdPPM = getColumnThresholdPPM(col);
  if (thresholdPPM === null || currentPrice <= 0) return null;

  const hasR2 = pointsRMinus2 !== null;
  const hasR1 = pointsRMinus1 !== null;
  const n = 1 + (hasR1 ? 1 : 0) + (hasR2 ? 1 : 0);

  return Math.ceil(
    n * thresholdPPM * currentPrice -
      (pointsRMinus2 ?? 0) -
      (pointsRMinus1 ?? 0),
  );
}

/** Human label for points-needed, e.g. "≥ 72" (rise) or "< 27" (drop). */
export function columnPointsNeededLabel(
  col: string,
  currentPrice: number,
  pointsRMinus2: number | null,
  pointsRMinus1: number | null,
): string {
  const pts = columnPointsNeeded(
    col,
    currentPrice,
    pointsRMinus2,
    pointsRMinus1,
  );
  if (pts === null) return "";
  const prefix = col.startsWith("+") ? "≥" : "<";
  return `${prefix} ${pts}`;
}

/** Sorted price-change columns for an item (ascending: most-negative first). */
export function getBudgetColumns(item: BudgetItem): string[] {
  if (item.tier === "A") return [...TIER_A_COLUMNS];
  return (Object.keys(item.priceChangeProbabilities) as string[]).sort(
    (a, b) => parseFloat(a) - parseFloat(b),
  );
}

export type ColumnKind = "rise" | "drop" | "neutral";

/** Classify a column by direction. */
export function columnKind(col: string): ColumnKind {
  if (col.startsWith("+")) return "rise";
  if (col.startsWith("-")) return "drop";
  return "neutral";
}

/** CSS colour token for a column direction. */
function kindToken(kind: ColumnKind): string {
  if (kind === "rise") return "var(--success)";
  if (kind === "drop") return "var(--destructive)";
  return "var(--muted-foreground)";
}

/**
 * Extremity (0..1) of a column within its tier — larger swings are darker.
 * e.g. for Tier A, +0.3 → 1.0, +0.1 → 0.33.
 */
export function columnExtremity(col: string, columns: string[]): number {
  const maxAbs = Math.max(
    ...columns.map((c) => Math.abs(parseFloat(c))),
    0.0001,
  );
  return Math.min(1, Math.abs(parseFloat(col)) / maxAbs);
}

/**
 * Solid colour for a verdict-bar segment: the bigger the swing, the more
 * saturated. Uses `color-mix` against the theme token so it tracks the active
 * light/dark palette.
 */
export function segmentColor(col: string, columns: string[]): string {
  const kind = columnKind(col);
  const extremity = columnExtremity(col, columns);
  const pct = Math.round((0.5 + 0.5 * extremity) * 100);
  return `color-mix(in oklch, ${kindToken(kind)} ${pct}%, transparent)`;
}

/**
 * Heatmap cell background for the compact table: opacity scales with the
 * probability (0–100), so high-odds cells pop like the reference design.
 */
export function heatCellColor(col: string, probability: number): string {
  const kind = columnKind(col);
  if (probability <= 0) return "transparent";
  // 8% floor so a non-zero cell is faintly tinted; ramp to ~62% at 100%.
  const pct = Math.round(8 + (probability / 100) * 54);
  return `color-mix(in oklch, ${kindToken(kind)} ${pct}%, transparent)`;
}

export interface VerdictSummary {
  /** Total probability mass (may differ slightly from 100 due to rounding). */
  total: number;
  /** Sum of positive-column probabilities. */
  riseProb: number;
  /** Sum of negative-column probabilities. */
  dropProb: number;
  /** Sum of neutral-column probabilities. */
  neutralProb: number;
  /** Per-column segments in ascending order with normalized widths (%). */
  segments: VerdictSegment[];
}

export interface VerdictSegment {
  col: string;
  kind: ColumnKind;
  probability: number;
  /** Width as a percentage of the full bar (normalized by total). */
  width: number;
  color: string;
}

/**
 * Magnitude (0..1) of a known price change relative to the tier's maximum
 * swing, for the results-known directional bar. Tier A swings up to ±0.3,
 * Tier B up to ±0.6.
 */
export function resultMagnitude(
  delta: number,
  tier: "A" | "B",
): { magnitude: number; max: number } {
  const max = tier === "A" ? 0.3 : 0.6;
  return { magnitude: Math.min(1, Math.abs(delta) / max), max };
}

/** Build the diverging-bar summary for an item. */
export function summarizeVerdict(item: BudgetItem): VerdictSummary {
  const columns = getBudgetColumns(item);
  const probs = columns.map((col) => item.priceChangeProbabilities[col] || 0);
  const total = probs.reduce((a, b) => a + b, 0);

  let riseProb = 0;
  let dropProb = 0;
  let neutralProb = 0;
  const segments: VerdictSegment[] = columns.map((col, i) => {
    const probability = probs[i];
    const kind = columnKind(col);
    if (kind === "rise") riseProb += probability;
    else if (kind === "drop") dropProb += probability;
    else neutralProb += probability;
    return {
      col,
      kind,
      probability,
      width: total > 0 ? (probability / total) * 100 : 0,
      color: segmentColor(col, columns),
    };
  });

  return { total, riseProb, dropProb, neutralProb, segments };
}
